import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Link } from "react-router-dom";
import {
  Flower2, Crown, Sparkles, Building2, Landmark, Check, ArrowRight,
} from "lucide-react";
import MembershipCheckout from "@/components/MembershipCheckout";
import Seo from "@/components/Seo";

const TIER_CONTENT = {
  essentials: {
    icon: Flower2,
    priceLabel: "Starting at $99/month",
    tagline: "Perfect for apartments, homes & gifts",
    benefits: [
      "Monthly floral delivery",
      "Flexible arrangement selection",
      "Priority ordering during holidays",
      "Exclusive member pricing",
    ],
  },
  signature: {
    icon: Sparkles,
    priceLabel: "Starting at $199/month",
    tagline: "For floral enthusiasts & luxury gifting",
    featured: true,
    benefits: [
      "Larger arrangements",
      "Premium blooms",
      "Priority delivery scheduling",
      "Seasonal upgrades",
      "Exclusive member designs",
    ],
  },
  concierge: {
    icon: Crown,
    priceLabel: "Starting at $399/month",
    tagline: "White-Glove Floral Concierge Service",
    benefits: [
      "White-glove delivery & in-home placement",
      "Packaging removal & vessel exchange",
      "Dedicated floral concierge",
      "Personalized floral planning",
      "VIP holiday access & event discounts",
      "Complimentary consultations",
    ],
  },
  corporate_floral: {
    icon: Building2,
    priceLabel: "Custom Pricing",
    tagline: "Offices · Hotels · Restaurants · Retail · Salons",
    benefits: [
      "Weekly, bi-weekly or monthly refreshes",
      "Reception & executive office florals",
      "Conference room arrangements",
      "Client gifting programs",
    ],
  },
  corporate_installation: {
    icon: Landmark,
    priceLabel: "Starting at $500+/month",
    tagline: "Floral walls, lobby & seasonal installations",
    benefits: [
      "Floral walls & hanging installations",
      "Lobby, entry & staircase displays",
      "Retail visual merchandising",
      "Holiday transformations",
      "Monthly, quarterly or seasonal scheduling",
    ],
  },
};

const TIER_ORDER = ["essentials", "signature", "concierge", "corporate_floral", "corporate_installation"];

const FLORAL_STYLES = [
  "Rose Collection", "White Rose Collection", "Hydrangea Collection", "Peony Collection",
  "Mixed Luxury Collection", "Garden Collection", "Fleuris Noir Signature Collection",
  "Designer's Choice", "Seasonal Collection",
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1724847764267-12775ec49657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3ZWRkaW5nJTIwZmxvcmFsJTIwZGVjb3J8ZW58MHx8fHwxNzc2MjYyMDM5fDA&ixlib=rb-4.1.0&q=85";

export default function Memberships() {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState("signature");
  const [activeStyle, setActiveStyle] = useState("Fleuris Noir Signature Collection");
  const checkoutRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/memberships/tiers`).then((res) => setTiers(res.data)).catch(() => {});
  }, []);

  const scrollToCheckout = (tierKey) => {
    if (tierKey) setSelectedTier(tierKey);
    setTimeout(() => checkoutRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const orderedTiers = TIER_ORDER
    .map((k) => tiers.find((t) => t.key === k))
    .filter(Boolean);

  return (
    <div className="page-transition pt-[72px]" data-testid="memberships-page">
      <Seo
        title="Luxury Flower Memberships & Subscriptions | Fleuris Noir — Minnesota"
        description="Luxury flower memberships in Minnesota. Recurring floral deliveries, corporate floral services & luxury installations from Fleuris Noir — Minneapolis luxury florist."
        path="/memberships"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Luxury Flower Subscription & Corporate Floral Services",
          provider: { "@type": "Florist", name: "Fleuris Noir", areaServed: "Minneapolis, Minnesota", telephone: "+1-612-547-9260" },
          areaServed: "Minnesota",
          offers: TIER_ORDER.map((k) => ({ "@type": "Offer", name: TIER_CONTENT[k] && k })),
        }}
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_IMG}')` }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0c0c0cf2 0%, #0c0c0ccc 45%, #0c0c0c66 100%)" }} />
        <div className="container-custom relative z-10 py-24">
          <div className="max-w-2xl">
            <p className="overline mb-5 animate-fade-in">Fleuris Noir Memberships</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#f8f5f0] leading-[1.05] mb-6 animate-fade-in animate-delay-100">
              Luxury Florals,<br /><span className="gold-gradient">Your Way.</span>
            </h1>
            <p className="font-body text-lg text-[#d8d3cc] leading-relaxed mb-10 max-w-xl animate-fade-in animate-delay-200">
              Enjoy recurring floral deliveries, custom designs, corporate floral services, and luxury installations
              tailored to your personal style and space.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animate-delay-300">
              <button onClick={() => scrollToCheckout()} className="btn-primary inline-flex items-center justify-center gap-2" data-testid="hero-become-member-btn">
                Become a Member <ArrowRight size={14} />
              </button>
              <Link to="/inquiries?subject=Membership%20Consultation" className="btn-secondary inline-flex items-center justify-center" data-testid="hero-consultation-btn">
                Schedule a Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="overline mb-4">Choose Your Membership</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#f8f5f0] mb-4">A Concierge for Every Space</h2>
            <p className="font-body text-[#a19d98]">From a single bloom-filled apartment to a full corporate installation program — composed, never ordinary.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orderedTiers.map((t, i) => {
              const c = TIER_CONTENT[t.key];
              const Icon = c.icon;
              return (
                <div
                  key={t.key}
                  className={`relative flex flex-col p-8 border transition-all animate-fade-in-up ${
                    c.featured
                      ? "border-[#b8966a] bg-[#161616]"
                      : "border-[#b8966a]/15 bg-[#121212] hover:border-[#b8966a]/40"
                  }`}
                  style={{ animationDelay: `${i * 0.06}s` }}
                  data-testid={`tier-card-${t.key}`}
                >
                  {c.featured && (
                    <span className="absolute -top-3 left-8 bg-[#b8966a] text-[#0c0c0c] px-3 py-1 font-sans text-[9px] tracking-[0.2em] uppercase">
                      Most Loved
                    </span>
                  )}
                  <Icon className="text-[#b8966a] mb-5" size={26} strokeWidth={1.4} />
                  <h3 className="font-heading text-2xl text-[#f8f5f0] mb-1">{t.name}</h3>
                  <p className="font-sans text-sm text-[#b8966a] mb-1">{c.priceLabel}</p>
                  <p className="font-body text-xs text-[#a19d98] mb-6">{c.tagline}</p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {c.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2.5">
                        <Check size={15} className="text-[#b8966a] mt-0.5 flex-shrink-0" />
                        <span className="font-body text-sm text-[#d8d3cc] leading-snug">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => scrollToCheckout(t.key)}
                    className={c.featured ? "btn-primary w-full" : "btn-secondary w-full"}
                    data-testid={`tier-select-${t.key}`}
                  >
                    {t.consultation ? "Request Consultation" : "Select Membership"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ARRANGEMENT SELECTION */}
      <section className="section bg-[#161616] border-y border-[#b8966a]/10">
        <div className="container-custom">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="overline mb-4">Curate Your Style</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#f8f5f0] mb-4">Choose Your Floral Style</h2>
            <p className="font-body text-[#a19d98]">
              Members are never limited to a single arrangement style. Select your favorites — or change your selections throughout your membership.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {FLORAL_STYLES.map((s) => (
              <button
                key={s}
                onClick={() => setActiveStyle(s)}
                className={`px-5 py-3 font-sans text-[11px] tracking-[0.15em] uppercase border transition-all ${
                  activeStyle === s
                    ? "bg-[#b8966a] border-[#b8966a] text-[#0c0c0c]"
                    : "bg-transparent border-[#b8966a]/20 text-[#a19d98] hover:border-[#b8966a]/50"
                }`}
                data-testid={`floral-style-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CHECKOUT */}
      <section ref={checkoutRef} className="section bg-[#0c0c0c]">
        <div className="container-custom max-w-3xl">
          <MembershipCheckout tiers={tiers} selectedTier={selectedTier} setSelectedTier={setSelectedTier} />
        </div>
      </section>
    </div>
  );
}
